/*
 * Quote sections, like Jobber's Add section (owner, 2026-10-31): parts the office adds to one quote for the client to see.
 * An introduction with a picture, photos of past work, files, and reviews. The client message stays on the quote itself.
 * Pictures and files are uploaded to the quote first (files with the subject "quote"), then a section lists them.
 */
import { z } from "zod";
import { dateString } from "./clients.zod.js";

export const QUOTE_SECTION_KINDS = ["intro", "images", "attachments", "reviews"] as const;
export type QuoteSectionKind = (typeof QUOTE_SECTION_KINDS)[number];

export const QUOTE_SECTION_WORDS: Record<QuoteSectionKind, string> = {
  intro: "Introduction",
  images: "Images",
  attachments: "Attachments",
  reviews: "Reviews",
};

/** Up to 10 pictures or files in one section, like Jobber. */
export const MAX_SECTION_FILES = 10;
export const MAX_QUOTE_REVIEWS = 6;

/** Pictures every browser shows. */
export const SECTION_IMAGE_TYPES = ["image/avif", "image/gif", "image/jpeg", "image/png", "image/webp"] as const;
/** Files the client can open or save: pictures, iPhone photos, PDF and Word. */
export const SECTION_FILE_TYPES = [
  ...SECTION_IMAGE_TYPES,
  "image/heic",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
] as const;

export const isSectionImage = (mime: string) => (SECTION_IMAGE_TYPES as readonly string[]).includes(mime);
export const isSectionFile = (mime: string) => (SECTION_FILE_TYPES as readonly string[]).includes(mime);

/** A review as the quote shows it: copied in, so it stays the same if the Google review changes. */
export const quoteReview = z.object({
  author: z.string().trim().min(1, "Type who wrote it.").max(80),
  stars: z.number().int().min(1).max(5),
  text: z.string().trim().min(1, "Type what they said.").max(1000),
  postedOn: dateString.nullable(),
});
export type QuoteReview = z.infer<typeof quoteReview>;

const fileIds = z.array(z.uuid()).max(MAX_SECTION_FILES, `The most is ${String(MAX_SECTION_FILES)}.`);

export const quoteSection = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("intro"),
    title: z.string().trim().max(200),
    body: z.string().trim().max(4000),
    imageId: z.uuid().nullable(),
  }),
  z.object({ kind: z.literal("images"), fileIds }),
  z.object({ kind: z.literal("attachments"), fileIds }),
  z.object({
    kind: z.literal("reviews"),
    reviews: z.array(quoteReview).max(MAX_QUOTE_REVIEWS, `The most is ${String(MAX_QUOTE_REVIEWS)} reviews.`),
  }),
]);
export type QuoteSection = z.infer<typeof quoteSection>;

export const quoteSections = z
  .array(quoteSection)
  .max(QUOTE_SECTION_KINDS.length)
  .refine((list) => new Set(list.map((s) => s.kind)).size === list.length, "Each section goes on a quote once.");

/** Saving the sections, with the client message (Jobber keeps it beside them). */
export const quoteSectionsBody = z.object({
  sections: quoteSections,
  message: z
    .string()
    .trim()
    .max(2000)
    .transform((s) => (s === "" ? null : s))
    .nullable(),
});
export type QuoteSectionsBody = z.infer<typeof quoteSectionsBody>;

/** The quote's name and number, from the pencil at the top of the quote page. */
export const quoteHeaderBody = z.object({
  title: z
    .string()
    .trim()
    .max(200)
    .transform((s) => (s === "" ? null : s))
    .nullable(),
  number: z.number().int().min(1).max(99_999_999),
});

/** A picture or file a section uses, as both the office and the client see it. */
export const sectionFile = z.object({ id: z.uuid(), name: z.string(), mime: z.string(), size: z.number() });
export type SectionFile = z.infer<typeof sectionFile>;

/** Every file the sections use, in the order they show. */
export function sectionFileIds(sections: readonly QuoteSection[]): string[] {
  return sections.flatMap((s) =>
    s.kind === "intro" ? (s.imageId ? [s.imageId] : []) : s.kind === "images" || s.kind === "attachments" ? s.fileIds : [],
  );
}

/** Keeps only sections with something in them, so an empty card never shows to the client. */
export function filledSections(sections: readonly QuoteSection[]): QuoteSection[] {
  return sections.filter((s) =>
    s.kind === "intro"
      ? Boolean(s.title || s.body || s.imageId)
      : s.kind === "reviews"
        ? s.reviews.length > 0
        : s.fileIds.length > 0,
  );
}
